import { Home as HomeIcon, Network, Video, type LucideIcon } from 'lucide-react'
import { Shell, useShellMeta } from './Shell.js'
import { Link } from '../router.js'
import { useIntegrationCatalog, IntegrationStatusPill } from '../navigation/integration-status.js'
import { CleAccesPanel } from './CleAccesPanel.js'
import { SondeCredentialPanel } from '../supervision/SondeCredentialPanel.js'

/**
 * ADMINISTRATION → INTÉGRATIONS → UniFi Network · UniFi Protect · Home
 * Assistant (15/10/2026).
 *
 * Avant cette page, leurs cartes du catalogue menaient… au catalogue
 * lui-même (« Configurer → » pointait /settings/integrations), et la seule
 * façon de changer leur clé était un formulaire caché de la page
 * Infrastructure, visible seulement APRÈS une panne d'authentification —
 * donc impossible de préparer une rotation sans d'abord casser la collecte.
 *
 * La SUPERVISION reste où elle est (Infrastructure : état, composants,
 * historique) ; ici vit ce qui se RÈGLE : la clé d'accès.
 *
 * SONDE (21/10/2026) : un SECOND panneau, « Accès de Sonde (compte
 * lecteur) », sous la clé de Gestion. Sonde lit l'équipement avec son propre
 * compte en lecture seule ; saisir sa clé ne remplace JAMAIS celle de Gestion,
 * qui reste en service tant que l'ancien code n'est pas retiré.
 */

type Cle = 'unifi_network' | 'unifi_protect' | 'home_assistant'

/** La source de Sonde qui lit le même équipement, avec son propre compte. */
const SOURCE_SONDE: Record<Cle, { code: string; compte: string }> = {
  unifi_network: { code: 'unifi_network', compte: 'une clé d’administrateur local en lecture seule, créée pour Sonde' },
  unifi_protect: { code: 'unifi_protect', compte: 'une clé Protect propre à Sonde' },
  home_assistant: { code: 'home_assistant', compte: 'le jeton d’un compte Home Assistant « sonde » non administrateur' },
}

const FICHES: Record<Cle, { nom: string; icone: LucideIcon; source: string; quoi: string }> = {
  unifi_network: { nom: 'UniFi Network', icone: Network, source: 'unifi-network', quoi: 'Gestion lit l’état du réseau (passerelle, bornes WiFi, switchs, clients) — jamais la configuration, et n’écrit rien.' },
  unifi_protect: { nom: 'UniFi Protect', icone: Video, source: 'unifi-protect', quoi: 'Gestion lit l’état des caméras (en ligne ou non) — aucune image, aucun enregistrement n’entre dans Gestion.' },
  home_assistant: { nom: 'Home Assistant', icone: HomeIcon, source: 'home-assistant', quoi: 'Gestion vérifie que Home Assistant fonctionne et compte ses entités — il ne commande rien.' },
}

export function InfraIntegrationPage({ integration }: { integration: Cle }) {
  const meta = useShellMeta()
  const catalogue = useIntegrationCatalog()
  const canon = catalogue?.find((i) => i.id === integration) ?? null
  const f = FICHES[integration]
  const Icone = f.icone
  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-stagger ic-page cle-page">
        <Link className="lc-crumb" href="/settings/integrations">← Intégrations</Link>
        <header className="cm-head">
          <div>
            <h1 className="lc-title cle-titre"><Icone size={22} aria-hidden="true" /> {canon?.name ?? f.nom}</h1>
            <p className="lc-subtitle cle-sous">
              {canon ? <IntegrationStatusPill status={canon.status} label={canon.status_label} /> : null}
              <span>{f.quoi}</span>
            </p>
            {canon?.status_detail ? <p className="lc-hint">{canon.status_detail}</p> : null}
            <p className="lc-hint">
              État détaillé, composants et historique : <Link href={`/infrastructure/${f.source}`}>Infrastructure → {canon?.name ?? f.nom}</Link>
            </p>
          </div>
        </header>
        <CleAccesPanel integration={integration} />
        <SondeCredentialPanel
          source={SOURCE_SONDE[integration].code}
          titre="Accès de Sonde (compte lecteur)"
          intro={`Sonde lit ${canon?.name ?? f.nom} avec son propre compte : ${SOURCE_SONDE[integration].compte}. Cette clé ne remplace pas celle de Gestion ci-dessus, qui reste en service.`}
        />
      </main>
    </Shell>
  )
}
